<template>
  <div v-if="show">
    <overlay :open="show" @close="close" />
    <div class="geo-checker">
      <button class="close" @click="close"><i class="fal fa-times" /></button>
      <h3 class="title">{{ headline }}</h3>
      <ul class="list">
        <li v-for="(shop, index) in otherShops" :key="index" class="list-item">
          <a :href="shop.url" @click.prevent="goto(shop)">{{ shop.name }}</a>
        </li>
      </ul>
      <button class="button" @click="close">
        {{ buttonLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import Overlay from "./overlay.vue";

const URL_PARAM_SKIP_KEY = "skip-geo";
const LS_GEO_KEY = "puori-geo-00002";
const LS_GEO_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// for each store we have a function the return true if the store
// is the correct store for the users given country/continent
const storePredicates = {
  dk(country, continent) {
    return country === "dk";
  },
  gb(country, continent) {
    return country === "gb";
  },
  us(country, continent) {
    return continent === "na" || continent === "sa";
  },
  eu(country, continent) {
    if (country === "dk") return false;
    return continent === "eu";
  },
  default() {
    return false;
  },
};

const shops = location.host.startsWith("wholesale")
  ? [
      {
        name: "EN / EUR",
        url: "https://wholesale.puori.eu",
        countryCode: "eu",
      },
      {
        name: "EN / USD",
        url: "https://wholesale.puori.com",
        countryCode: "us",
      },
      {
        name: "DA / DKK",
        url: "https://wholesale.puori.dk",
        countryCode: "dk",
      },
      {
        name: "SE / SEK",
        url: "https://wholesale.se.puori.com/",
        countryCode: "se",
      },
    ]
  : [
      {
        name: "EN / EUR",
        url: "https://puori.eu",
        countryCode: "eu",
      },
      {
        name: "EN / GBP",
        url: "https://puori.co.uk",
        countryCode: "gb",
      },
      {
        name: "EN / USD",
        url: "https://puori.com",
        countryCode: "us",
      },
      {
        name: "DA / DKK",
        url: "https://puori.dk",
        countryCode: "dk",
      },
    ];

function getPredicateFunction(countryCode) {
  return storePredicates[countryCode] || storePredicates.default;
}

export default {
  name: "GeoChecker",
  selector: "#vue-geo-checker",
  components: { Overlay },
  props: {
    headline: {
      type: String,
      required: true,
    },
    buttonLabel: {
      type: String,
      required: true,
    },
    countryCode: {
      type: String,
      required: true,
    },
    excludes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      show: false,
      shops: shops,
    };
  },
  methods: {
    goto(shop) {
      window.open(`${shop.url}?${URL_PARAM_SKIP_KEY}`, "_self");
    },
    close() {
      localStorage.setItem(LS_GEO_KEY, Date.now());
      this.show = false;
    },
    setupToken() {
      window.localStorage.setItem(LS_GEO_KEY, Date.now());
    },
    isExcluded() {
      for (const exclude of this.excludes) {
        if (window.location.pathname === exclude) {
          return true;
        }
      }
      return false;
    },
  },
  computed: {
    otherShops() {
      return this.shops.filter(
        (shop) =>
          shop.countryCode.toLowerCase() !== this.countryCode.toLowerCase()
      );
    },
  },
  async created() {
    // see the geo-targeting.md file for easier to parse logic ✌️
    const triggers = document.querySelectorAll('[data-geo-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.show = true
      })
    })

    // If current path name is in the exclude, do nothing
    if (this.isExcluded()) {
      return;
    }

    // A
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has(URL_PARAM_SKIP_KEY)) {
      this.setupToken();
      urlParams.delete(URL_PARAM_SKIP_KEY);
      const updatedUrl = `${window.location.pathname}?${urlParams}${
        window.location.hash
      }`;
      window.history.replaceState({}, document.title, updatedUrl);
      return;
    }

    // B
    const timestamp = localStorage.getItem(LS_GEO_KEY);
    const now = Date.now();
    if (timestamp && now - timestamp < LS_GEO_EXPIRY) {
      this.setupToken();
      return;
    }

    // C
    const { data } = await this.$http(
      "https://ipapi.co/json/?key=86RKX1jPTVARaBZAMZH9u5wJUY1tzEXn6zLSkiWuiqpnDerK4c"
    );
    const country = data.country_code.toLowerCase();
    const continent = data.continent_code.toLowerCase();
    const siteCountryCode = this.countryCode.toLowerCase();

    const predicate = getPredicateFunction(siteCountryCode);
    if (predicate(country, continent)) {
      this.setupToken();
      return;
    }

    this.show = true;
  },
  watch: {
    show() {
      document.body.style.overflow = this.show ? "hidden" : "";
    },
  },
};
</script>

<style lang="scss" scoped>
.geo-checker {
  max-width: 500px;
  padding: 48px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 62;
  background-color: $white;
  text-align: center;
  width: 100%;

  @include media("<=tablet") {
    padding: 24px;
  }
}

.close {
  position: absolute;
  top: 24px;
  right: 24px;
  font-size: 24px;
  @include button-reset();

  @include media("<=tablet") {
    top: 16px;
    right: 16px;
  }
}

.title {
  margin: 0;
}

.list {
  padding: 0;
  margin: 0;
  margin-top: 64px;
  list-style: none;

  > * + * {
    margin-top: 16px;
  }

  &-item {
    font-size: 18px;
  }

  a {
    font-weight: 700;
    color: $black;
    text-decoration: none;
    &:hover {
      color: $teal;
    }
  }
}

.button {
  margin-top: 64px;
}
</style>
